import React, { useEffect, useRef } from 'react';
import { BellIcon } from '../../../components/icons';
import { Button } from '../../../components/ui/Button';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { useNotifications } from '../../../hooks/useNotifications';
import {
  isIos,
  isStandalonePwa,
  isWebPushSupported,
} from '../../../services/modules/push/push.helpers';
import { notify } from '../../../utils/toast';

const vapidPublicKey = import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined;

export const PushNotificationsCard: React.FC = () => {
  const { currentOrganization } = useOrganization();
  const {
    permission,
    pushEnabled,
    isPushLoading,
    enablePush,
    disablePush,
    reconcilePushRegistration,
  } = useNotifications();

  const unsupported = !isWebPushSupported() || !vapidPublicKey;
  const iosNeedsInstall = isIos() && !isStandalonePwa();
  const denied = permission === 'denied';
  const healedRef = useRef(false);

  // Inscrição local órfã (permissão ok no iPhone, mas 401 no servidor) → reenvia.
  useEffect(() => {
    if (!currentOrganization?.id || unsupported || iosNeedsInstall || denied) return;
    if (healedRef.current) return;

    let cancelled = false;
    void (async () => {
      const result = await reconcilePushRegistration(currentOrganization.id);
      if (cancelled || result !== 'healed') return;
      healedRef.current = true;
      notify.success('Notificações sincronizadas. Feche o app e peça um teste de mensagem.');
    })();

    return () => {
      cancelled = true;
    };
  }, [
    currentOrganization?.id,
    unsupported,
    iosNeedsInstall,
    denied,
    reconcilePushRegistration,
  ]);

  const handleToggle = async () => {
    if (!currentOrganization?.id) {
      notify.error('Organização não carregada. Tente novamente.');
      return;
    }

    if (pushEnabled) {
      try {
        await disablePush();
        notify.info('Notificações desativadas.');
      } catch {
        notify.error('Não foi possível desativar. Tente novamente.');
      }
      return;
    }

    try {
      const result = await enablePush(currentOrganization.id);
      if (result === 'granted') {
        notify.success('Notificações ativadas. Feche o app e peça um teste de mensagem.');
        return;
      }
      if (result === 'denied') {
        notify.error('Permissão negada. Ative em Ajustes → Notificações → Adlitics.');
        return;
      }
      notify.error('Push indisponível neste dispositivo.');
    } catch (error) {
      const detail = error instanceof Error ? error.message : '';
      notify.error(
        detail
          ? `Falha ao ativar: ${detail}`
          : 'Falha ao ativar. Abra pelo ícone da Tela de Início e tente de novo.',
      );
    }
  };

  return (
    <div className="card-premium p-4">
      <div className="flex items-start gap-3">
        <span className="p-2 bg-surface rounded-lg text-secondary">
          <BellIcon className="w-4 h-4" />
        </span>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-bold text-secondary">Notificações Push</h3>
          <p className="text-xs text-muted mt-1">
            Receba alertas de novas mensagens e tarefas mesmo com o app fechado.
          </p>

          <div className="mt-4">
            {unsupported ? (
              <p className="text-xs text-muted">Indisponível neste navegador</p>
            ) : iosNeedsInstall ? (
              <p className="text-xs text-muted">
                No iPhone, abra o Adlitics pelo ícone da Tela de Início (não pelo Safari) e ative aqui.
              </p>
            ) : denied ? (
              <p className="text-xs text-muted">
                Bloqueado. Vá em Ajustes → Notificações → Adlitics e permita.
              </p>
            ) : (
              <Button
                type="button"
                onClick={() => void handleToggle()}
                disabled={isPushLoading || !currentOrganization?.id}
                className="min-h-11 touch-manipulation sm:min-h-0"
              >
                {isPushLoading
                  ? 'Processando...'
                  : pushEnabled
                    ? 'Desativar'
                    : 'Ativar'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
